import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMAGE = "/hero-bg.png";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-screen overflow-hidden flex items-end"
    >
      {/* Parallax Background */}
      <motion.div
        className="absolute inset-0 z-0"
        style={{ y }}
      >
        <img
          src={HERO_IMAGE}
          alt="LMT Spark creative studio"
          className="w-full h-[120%] object-cover"
        />
      </motion.div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-deep-black/70 via-deep-black/30 to-transparent" />

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24"
      >
        <div className="flex flex-col">
          {/* Pill Label */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span
              data-testid="hero-pill"
              className="inline-block font-body text-xs uppercase tracking-[0.2em] text-white/80 border border-white/30 rounded-full px-5 py-2 mb-8"
            >
              Creative & Commercial Consultancy
            </span>
          </motion.div>

          {/* Logo Heading */}
          <motion.div
            data-testid="hero-heading"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="mb-8"
          >
            <h1 className="font-heading text-6xl sm:text-7xl md:text-8xl lg:text-9xl text-white tracking-[-0.01em] leading-[0.9] uppercase">
              <span className="font-bold">LMT</span>{" "}
              <span className="font-light">SPARK</span>
            </h1>
            <p className="font-heading text-xl sm:text-2xl md:text-3xl lg:text-4xl text-white/80 tracking-[0.15em] uppercase font-extralight mt-2">
              LIMITLESS SPARK
            </p>
          </motion.div>

          {/* Subtext */}
          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="font-body text-base md:text-lg text-white/70 max-w-xl leading-relaxed font-light"
          >
            Product design, branding, packaging and retail strategy for consumer
            products and toys — shaped with editorial precision and commercial
            clarity.
          </motion.p>
        </div>
      </motion.div>
    </section>
  );
};
